import { useState } from 'react';
import {
  makeStyles, tokens, Button, Text, MessageBar, MessageBarBody, MessageBarTitle,
} from '@fluentui/react-components';
import { BranchFork20Regular } from '@fluentui/react-icons';
import { useApi } from '../api/context';
import type { Framework } from '../api/client';

const useStyles = makeStyles({
  wrap: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalXS },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
});

/**
 * Builds a regulator's section list from the SCF's cross mappings.
 *
 * For MAS TRM and BNM RMiT there is no alternative. Both are published as PDFs,
 * and MAS states the guidelines are not meant to be prescriptive, so there is
 * no numbered catalogue to fetch even in principle. The SCF has already read
 * them and recorded which sections each of its controls satisfies.
 *
 * Derivation is a derivative work, so it is refused unless a commercial licence
 * has been recorded against the SCF. The refusal explains itself rather than
 * failing quietly.
 */
export function FrameworkDerive({ framework, onDerived }: {
  framework: Framework;
  onDerived: () => void;
}) {
  const styles = useStyles();
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ sectionCount: number; warnings: string[] } | null>(null);

  const run = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await api.deriveFramework(framework.frameworkId);
      setResult(r);
      onDerived();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.wrap}>
      <Button size="small" icon={<BranchFork20Regular />} disabled={busy} onClick={() => void run()}>
        {busy ? 'Deriving' : framework.contentLoaded ? 'Rebuild from SCF' : 'Build from SCF'}
      </Button>

      {result && (
        <MessageBar intent="success">
          <MessageBarBody>
            <MessageBarTitle>{result.sectionCount} sections</MessageBarTitle>
            {result.warnings.map((w, i) => <div key={i} className={styles.muted}>{w}</div>)}
          </MessageBarBody>
        </MessageBar>
      )}

      {error && (
        <MessageBar intent="warning"><MessageBarBody>{error}</MessageBarBody></MessageBar>
      )}

      {!result && !error && (
        <Text className={styles.muted}>
          No machine readable release exists for this regulator. Built from the SCF's
          cross mappings, which needs an SCF licence.
        </Text>
      )}
    </div>
  );
}
